import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Plus, Building2 } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { GoldButton } from "@/components/common/Buttons";
import { ORG, IMAGES } from "@/lib/content";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";

function BrandNode({ b }) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-white font-medium">{b.name}</div>
        {b.website && <ArrowUpRight className="h-4 w-4 text-gold/70 group-hover/brand:text-gold transition-colors" />}
      </div>
      <div className="text-xs text-slate-400 mt-0.5">{b.desc}</div>
      {b.website && <div className="text-[11px] text-gold/70 mt-1">{b.website.replace("https://", "")}</div>}
    </>
  );
  const cls = "group/brand block rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 hover:border-[var(--gold)]/40 transition-colors";
  return b.website ? (
    <a href={b.website} target="_blank" rel="noreferrer" className={cls} data-testid={`brand-${b.name}`}>{inner}</a>
  ) : (
    <div className={cls} data-testid={`brand-${b.name}`}>{inner}</div>
  );
}

function Organigram() {
  return (
    <div className="rounded-2xl border border-white/10 card-lux p-6 sm:p-10" data-testid="organigram">
      <div className="text-center">
        <div className="inline-block rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/[0.05] px-8 py-4">
          <div className="text-chrome font-serif text-2xl tracking-[0.18em]">LOSTA GROUP</div>
          <div className="eyebrow mt-1 text-[10px]">Holding</div>
        </div>
      </div>
      <div className="mx-auto my-6 h-8 w-px bg-white/15" />
      <div className="grid gap-8 lg:grid-cols-2">
        {ORG.poles.map((pole) => (
          <div key={pole.country} className="rounded-2xl border border-white/10 bg-[#0B0D13] p-6">
            <div className="text-center">
              <div className="eyebrow mb-2">{pole.country} {pole.flag}</div>
              {pole.companyWebsite ? (
                <a href={pole.companyWebsite} target="_blank" rel="noreferrer" className="font-serif text-2xl text-gold hover:underline inline-flex items-center gap-1.5">
                  {pole.company} <ArrowUpRight className="h-4 w-4" />
                </a>
              ) : (
                <div className="font-serif text-2xl text-gold">{pole.company}</div>
              )}
              <div className="text-xs text-slate-500 mt-1">Société opérationnelle</div>
            </div>
            <div className="mx-auto my-5 h-6 w-px bg-white/15" />
            <div className="grid gap-3">
              {pole.brands.map((b) => <BrandNode key={b.name} b={b} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Societes() {
  useSeo({
    title: "Nos Sociétés & Marques — Luxury Signs & Losta Tech | Losta Group",
    description:
      "Découvrez la structure de Losta Group : pôle Suisse Luxury Signs Sàrl, pôle France Losta Tech SAS, et leurs marques spécialisées — Genève Enseigne, SignaPharma, Smart X Glass, Portail du Léman, Rhône-Alpes Enseigne.",
    path: "/societes",
  });

  const [companies, setCompanies] = useState([]);
  const [country, setCountry] = useState("Tous");
  const [metier, setMetier] = useState("Tous");

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const metiers = useMemo(() => ["Tous", ...Array.from(new Set(companies.map((c) => c.metier)))], [companies]);
  const filtered = companies.filter(
    (c) => (country === "Tous" || c.country === country) && (metier === "Tous" || c.metier === metier)
  );
  const acquisitions = companies.filter((c) => c.kind === "acquisition");

  return (
    <>
      <PageHero
        eyebrow="Nos sociétés & marques"
        lines={["Une vision de groupe.", "Une expérience du terrain."]}
        sub="Losta Group développe sa stratégie autour de structures opérationnelles implantées en France et en Suisse, dans les métiers de la communication visuelle et les activités techniques complémentaires."
        image={IMAGES.facade}
      />

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHeading center eyebrow="Structure du groupe" lines={["La holding, ses sociétés", "et ses marques."]} className="mb-14" />
          <Reveal><Organigram /></Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 text-center font-serif font-light text-2xl sm:text-4xl text-white max-w-4xl mx-auto leading-snug">
              Nous ne regardons pas nos métiers de l'extérieur.
              <span className="block text-gold">Nous les pratiquons.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filterable companies */}
      <section className="py-16 sm:py-24 bg-[#0B0D13] border-y border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHeading eyebrow="Nos marques" lines={["Les sociétés", "du réseau."]} className="mb-10" />
          <div className="mb-10 flex flex-wrap gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 mr-1">Pays</span>
              {["Tous", "France", "Suisse"].map((c) => (
                <button key={c} onClick={() => setCountry(c)} data-testid={`filter-country-${c}`}
                  className={`rounded-full border px-4 py-1.5 text-xs transition-all ${country === c ? "border-[var(--gold)] text-gold bg-[var(--gold)]/10" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 mr-1">Métier</span>
              {metiers.map((m) => (
                <button key={m} onClick={() => setMetier(m)} data-testid={`filter-metier-${m}`}
                  className={`rounded-full border px-4 py-1.5 text-xs transition-all ${metier === m ? "border-[var(--gold)] text-gold bg-[var(--gold)]/10" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="companies-grid">
            {filtered.map((c, i) => (
              <Reveal key={c.id || i} delay={(i % 3) * 0.05}>
                <div className="group h-full overflow-hidden rounded-2xl border border-white/10 card-lux">
                  <div className="relative h-44 overflow-hidden">
                    {c.photo_url && <img src={c.photo_url} alt={c.name} loading="lazy" className="img-duotone h-full w-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F1118] to-transparent" />
                    <span className="absolute top-3 left-3 rounded-full border border-white/15 bg-black/40 px-3 py-1 text-[11px] text-white backdrop-blur">
                      {c.country} · {c.city}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="eyebrow !text-slate-400 mb-2">{c.metier}</div>
                    <h3 className="font-serif text-2xl text-white">{c.name}</h3>
                    <p className="mt-2 text-sm text-[var(--txt-2)] font-light leading-relaxed line-clamp-3">{c.description}</p>
                    <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                      <span>{c.pole}</span>
                      {c.website ? (
                        <a href={c.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gold">Découvrir <ArrowUpRight className="h-3.5 w-3.5" /></a>
                      ) : <span className="text-slate-600">Marque du groupe</span>}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && <p className="text-center text-slate-500 py-10">Aucune société pour ce filtre.</p>}
        </div>
      </section>

      {/* Acquisitions joined */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHeading eyebrow="Entreprises ayant rejoint le groupe" lines={["Demain,", "de nouveaux savoir-faire."]} className="mb-12" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {acquisitions.map((c, i) => (
              <Reveal key={c.id || i}>
                <div className="rounded-2xl border border-[var(--gold)]/30 card-lux p-6">
                  <Building2 className="h-6 w-6 text-gold" />
                  <h3 className="mt-4 font-serif text-2xl text-white">{c.name}</h3>
                  <p className="text-sm text-slate-400">{c.city} · {c.country} · {c.year}</p>
                  <p className="mt-2 text-sm text-[var(--txt-2)] font-light">{c.description}</p>
                </div>
              </Reveal>
            ))}
            {[0, 1, 2].slice(0, Math.max(0, 3 - acquisitions.length)).map((k) => (
              <Reveal key={`f${k}`} delay={k * 0.06}>
                <div className="flex h-full min-h-[180px] flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/[0.01] p-6 text-center" data-testid={`future-slot-${k}`}>
                  <Plus className="h-7 w-7 text-slate-600" />
                  <div className="mt-3 text-sm text-slate-500">Future acquisition</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 rounded-2xl border border-white/10 card-lux p-8 sm:p-12 text-center radial-gold">
              <p className="mx-auto max-w-2xl text-base sm:text-lg text-slate-300 font-light leading-relaxed">
                Chaque nouvelle entreprise doit renforcer le réseau tout en conservant son expertise, son histoire et sa
                proximité avec ses clients.
              </p>
              <div className="mt-8"><GoldButton to="/ceder-votre-entreprise" testid="societes-cta">Vous souhaitez rejoindre le groupe ?</GoldButton></div>
              <p className="mt-4 text-xs text-slate-500">Échange direct, confidentiel et sans engagement.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
